/**
 * LadiesHubSearch - Premium Search Bar with Clear (X) + Search History Dropdown
 * Features: Glass UI, RTL support, localStorage history, clear button
 * V2: Fixed dropdown close behavior (click-away, ESC, Android back)
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Clock, Trash2, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getAllSubcategoriesFlat, getChipBarTabs } from "../hubs/ladies/LadiesHubPremiumConfig";

/* ------------------------------------------------------------------
   Search History — per-user localStorage, v2 scoping.
   Same anti-leak rules as the rest of the app: real userId required,
   `"guest"` / empty / null rejected. Anonymous users do not persist.
   Legacy `ladies_search_history_v1` is swept at login / logout from
   AuthContext.
   ------------------------------------------------------------------ */
const HISTORY_PREFIX_V2 = "ladies_search_history_v2_";
const MAX_HISTORY = 12;
const MAX_DISPLAY = 8;

function isValidUserId(userId) {
  if (typeof userId !== "string") return false;
  const trimmed = userId.trim();
  if (!trimmed) return false;
  if (trimmed === "guest") return false;
  return true;
}

function getHistoryKey(userId) {
  return isValidUserId(userId) ? `${HISTORY_PREFIX_V2}${userId}` : null;
}

function readHistory(userId) {
  const key = getHistoryKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((v) => typeof v === "string" && v) : [];
  } catch {
    return [];
  }
}

function writeHistory(userId, list) {
  const key = getHistoryKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify((list || []).slice(0, MAX_HISTORY)));
  } catch {
    /* ignore */
  }
}

// Gold/Champagne color palette
const COLORS = {
  gold: "#C8A24D",
  goldLight: "rgba(200, 162, 77, 0.15)",
  border: "#E8DFCF",
  borderFocus: "#C8A24D",
  surface: "#FFFFFF",
  surfaceAlt: "#FAF8F5",
  ink: "#1C1C1C",
  muted: "#6A6A6A",
};

export default function LadiesHubSearch({
  value = "",
  onChange,
  onSubmit,
  placeholder,
  lang = "ar",
}) {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const isArabic = lang === "ar";
  const { user } = useAuth();
  const userId = user?.user_id || user?.id;

  // Per-user history state. Reset + re-read whenever the logged-in user
  // changes so a device shared between accounts can never leak history.
  const [history, setHistory] = useState(() => readHistory(userId));
  useEffect(() => {
    setHistory(readHistory(userId));
  }, [userId]);

  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const trimmed = (value || "").trim();

  // Filter history based on current input
  const filteredHistory = useMemo(() => {
    if (!trimmed) return history.slice(0, MAX_DISPLAY);
    const q = trimmed.toLowerCase();
    return history.filter((h) => h.toLowerCase().includes(q)).slice(0, MAX_DISPLAY);
  }, [history, trimmed]);

  // Suggestions for empty search
  const suggestions = useMemo(() => {
    return isArabic 
      ? ["فساتين", "مكياج", "عبايات", "ذهب", "خدمات تجميل"]
      : ["Dresses", "Makeup", "Abayas", "Gold", "Beauty Services"];
  }, [isArabic]);

  // Dynamic prefix-matched suggestions while typing (from category data)
  const typingSuggestions = useMemo(() => {
    if (!trimmed || trimmed.length < 2) return [];
    const q = trimmed.toLowerCase();
    const lang = isArabic ? 'ar' : 'en';
    const seen = new Set();
    const results = [];
    
    // 1. Prefix match on main category labels
    const tabs = getChipBarTabs();
    for (const tab of tabs) {
      if (tab.id === 'all') continue;
      const label = isArabic ? tab.label_ar : tab.label_en;
      if (label.toLowerCase().startsWith(q) && !seen.has(label)) {
        seen.add(label);
        results.push(label);
        if (results.length >= 5) return results;
      }
    }
    
    // 2. Prefix match on subcategory labels
    const subs = getAllSubcategoriesFlat(lang);
    for (const sub of subs) {
      if (sub.subLabel.toLowerCase().startsWith(q) && !seen.has(sub.subLabel)) {
        seen.add(sub.subLabel);
        results.push(sub.subLabel);
        if (results.length >= 5) return results;
      }
    }
    
    // 3. Fallback: contains match on subcategories (if < 5 results)
    for (const sub of subs) {
      if (sub.subLabel.toLowerCase().includes(q) && !seen.has(sub.subLabel)) {
        seen.add(sub.subLabel);
        results.push(sub.subLabel);
        if (results.length >= 5) return results;
      }
    }
    
    return results;
  }, [trimmed, isArabic]);

  const copy = {
    placeholder: isArabic ? "ابحثي عن منتج أو خدمة..." : "Search products or services...",
    historyTitle: isArabic ? "سجل البحث" : "Search History",
    clearAll: isArabic ? "مسح الكل" : "Clear All",
    noHistory: isArabic ? "لا يوجد سجل بحث بعد" : "No search history yet",
    suggestions: isArabic ? "اقتراحات" : "Suggestions",
    clearSearch: isArabic ? "مسح البحث" : "Clear search",
  };

  // Close dropdown function
  const closeDropdown = useCallback(() => {
    setOpen(false);
  }, []);

  function addToHistory(term) {
    const t = (term || "").trim();
    if (!t) return;
    if (!isValidUserId(userId)) return; // anonymous — do not persist
    setHistory((prev) => {
      const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, MAX_HISTORY);
      writeHistory(userId, next);
      return next;
    });
  }

  function clearHistory() {
    setHistory([]);
    if (isValidUserId(userId)) {
      const key = getHistoryKey(userId);
      if (key) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    }
  }

  function removeHistoryItem(term) {
    setHistory((prev) => {
      const next = prev.filter((x) => x !== term);
      if (isValidUserId(userId)) writeHistory(userId, next);
      return next;
    });
  }

  function handleSubmit(e) {
    e?.preventDefault();
    if (trimmed) {
      addToHistory(trimmed);
      closeDropdown();
      onSubmit?.(trimmed);
    }
  }

  function handleSelect(term) {
    onChange?.(term);
    addToHistory(term);
    closeDropdown();
    onSubmit?.(term);
  }

  function handleClear() {
    onChange?.("");
    inputRef.current?.focus();
    setOpen(true);
  }

  function handleFocus() {
    setIsFocused(true);
    setOpen(true);
  }

  function handleBlur() {
    setIsFocused(false);
  }

  // 1. Click-away to close (using pointerdown for better mobile support)
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [closeDropdown]);

  // 2. ESC key to close
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        closeDropdown();
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeDropdown]);

  // 3. Android Back / Browser Back to close first (don't navigate away)
  useEffect(() => {
    if (!open) return;

    // Push state when opening so back button can close it
    window.history.pushState({ modal: "searchSuggestionsOpen" }, "");

    const onPopState = () => {
      // Close dropdown instead of navigating
      closeDropdown();
    };

    window.addEventListener("popstate", onPopState);
    
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [open, closeDropdown]);

  const hasContent = history.length > 0 || suggestions.length > 0 || typingSuggestions.length > 0;

  return (
    <div 
      ref={wrapRef} 
      dir={isArabic ? "rtl" : "ltr"} 
      style={{ position: "relative", maxWidth: 520, margin: "0 auto" }}
      data-testid="ladies-search-container"
    >
      {/* Search Form */}
      <form 
        onSubmit={handleSubmit} 
        style={{
          position: "relative",
          background: COLORS.surface,
          border: `1px solid ${isFocused ? COLORS.borderFocus : COLORS.border}`,
          borderRadius: 16,
          boxShadow: isFocused 
            ? "0 4px 24px rgba(200, 162, 77, 0.12)" 
            : "0 4px 20px rgba(0,0,0,0.04)",
          height: 52,
          display: "flex",
          alignItems: "center",
          paddingLeft: isArabic ? 14 : 16,
          paddingRight: isArabic ? 16 : 14,
          gap: 10,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        {/* Search Icon */}
        <span 
          style={{ 
            color: COLORS.muted, 
            display: "flex",
            flexShrink: 0,
          }}
        >
          <Search size={18} />
        </span>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={() => setOpen(true)}
          placeholder={placeholder || copy.placeholder}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            fontSize: 14,
            background: "transparent",
            color: COLORS.ink,
            direction: isArabic ? "rtl" : "ltr",
          }}
          data-testid="ladies-search-input"
        />

        {/* Clear (X) Button */}
        <AnimatePresence>
          {value && value.length > 0 && (
            <motion.button
              type="button"
              onClick={handleClear}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              aria-label={copy.clearSearch}
              style={{
                border: "none",
                background: "rgba(0,0,0,0.06)",
                width: 28,
                height: 28,
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.1)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.06)"}
              data-testid="ladies-search-clear"
            >
              <X size={14} color={COLORS.muted} />
            </motion.button>
          )}
        </AnimatePresence>
      </form>

      {/* History & Suggestions Dropdown */}
      <AnimatePresence>
        {open && hasContent && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "absolute",
              top: 58,
              left: 0,
              right: 0,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 16,
              boxShadow: "0 14px 40px rgba(0,0,0,0.10)",
              overflow: "hidden",
              zIndex: 100,
            }}
            data-testid="ladies-search-dropdown"
          >
            {/* Dynamic typing suggestions (prefix-matched categories) */}
            {trimmed && typingSuggestions.length > 0 && (
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${COLORS.surfaceAlt}` }} data-testid="typing-suggestions">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {typingSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleSelect(s)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 20,
                        border: `1px solid ${COLORS.gold}`,
                        background: COLORS.goldLight,
                        color: COLORS.ink,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(200, 162, 77, 0.25)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = COLORS.goldLight;
                      }}
                      data-testid={`typing-suggestion-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions Section (when no input) */}
            {!trimmed && suggestions.length > 0 && (
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${COLORS.surfaceAlt}` }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 10,
                  color: COLORS.muted,
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  <Sparkles size={12} color={COLORS.gold} />
                  <span>{copy.suggestions}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleSelect(s)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 20,
                        border: `1px solid ${COLORS.border}`,
                        background: COLORS.surfaceAlt,
                        color: COLORS.ink,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = COLORS.goldLight;
                        e.currentTarget.style.borderColor = COLORS.gold;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = COLORS.surfaceAlt;
                        e.currentTarget.style.borderColor = COLORS.border;
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* History Section */}
            {history.length > 0 && (
              <>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderBottom: `1px solid ${COLORS.surfaceAlt}`,
                  color: COLORS.muted,
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Clock size={12} />
                    <span>{copy.historyTitle}</span>
                  </div>
                  <button
                    type="button"
                    onClick={clearHistory}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      color: COLORS.gold,
                      fontWeight: 700,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                    data-testid="ladies-search-clear-history"
                  >
                    <Trash2 size={12} />
                    {copy.clearAll}
                  </button>
                </div>

                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {filteredHistory.length === 0 ? (
                    <div style={{ padding: 14, color: COLORS.muted, fontSize: 13 }}>
                      {copy.noHistory}
                    </div>
                  ) : (
                    filteredHistory.map((term) => (
                      <div
                        key={term}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          borderBottom: `1px solid ${COLORS.surfaceAlt}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleSelect(term)}
                          style={{
                            flex: 1,
                            textAlign: isArabic ? "right" : "left",
                            padding: "12px 14px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontSize: 14,
                            transition: "background 0.15s ease",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = COLORS.surfaceAlt}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          <Clock size={14} color={COLORS.muted} />
                          <span style={{ flex: 1, color: COLORS.ink }}>{term}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeHistoryItem(term)}
                          aria-label="Remove"
                          style={{
                            padding: "8px 12px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: COLORS.muted,
                            opacity: 0.6,
                            transition: "opacity 0.15s ease",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = "0.6"}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
